import { motion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import carstenImage from "@/assets/team/carsten-meier.jpg";
import veitImage from "@/assets/team/veit-hailperin.jpg";

const contactEmails = ["carsten@4tagewoche.org", "veit@temporalabs.ch"];
const contactImages = [carstenImage, veitImage];
const contactWebsites = ["https://www.4tagewoche.org", "https://www.hailperin.ch"];

// Per-contact substring → URL mapping rendered inside the role line
const roleLinks: Record<string, string>[] = [
  {},
  {
    "4-Tage-Woche Schweiz": "https://4-tagewoche.ch",
    "4-Tage-Woche Österreich": "https://www.4-tagewoche.at",
    "Semaine de 4 jours Suisse": "https://4-tagewoche.ch",
    "Semaine de 4 jours Autriche": "https://www.4-tagewoche.at",
  },
];

function renderRoleWithLinks(role: string, links: Record<string, string>) {
  const keys = Object.keys(links);
  if (keys.length === 0) return role;
  // Build a regex that matches any of the keys (escaped). Longer first to avoid partial matches.
  const pattern = keys
    .sort((a, b) => b.length - a.length)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const regex = new RegExp(`(${pattern})`, "g");
  const parts = role.split(regex);
  return parts.map((part, i) => {
    const url = links[part];
    if (url) {
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-accent transition-colors underline-offset-2 hover:underline"
        >
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export function ContactSection() {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-primary">
      <div className="container px-4">
        {/* Fazit */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-3xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
            {t.contact.fazitTitle}
          </h2>
          <p className="text-lg text-primary-foreground/80 leading-relaxed">
            {t.contact.fazitText}
          </p>
        </motion.div>

        {/* 3 Phasen */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-4xl mx-auto mb-20"
        >
          <h3 className="text-xl font-semibold text-primary-foreground text-center mb-8">
            {t.contact.phasesTitle}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative">
            {t.contact.phases.map((phase) => (
              <div
                key={phase.number}
                className="relative bg-primary-foreground/5 rounded-2xl p-6 border border-primary-foreground/10"
              >
                <span className="text-4xl font-bold text-accent mb-4 block">{phase.number}</span>
                <h4 className="text-lg font-semibold text-primary-foreground mb-2">{phase.title}</h4>
                <p className="text-sm text-primary-foreground/70">{phase.description}</p>
              </div>
            ))}
            <ArrowRight className="hidden md:block absolute left-[calc(33.333%-12px)] top-1/2 -translate-y-1/2 h-6 w-6 text-accent z-10" />
            <ArrowRight className="hidden md:block absolute left-[calc(66.666%-12px)] top-1/2 -translate-y-1/2 h-6 w-6 text-accent z-10" />
          </div>
        </motion.div>

        {/* CTA und Kontakt */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-primary-foreground/5 rounded-2xl p-8 md:p-12 border border-primary-foreground/10">
            <div className="text-center mb-10">
              <h3 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
                {t.contact.ctaTitle}
              </h3>
              <p className="text-primary-foreground/70 max-w-xl mx-auto">
                {t.contact.ctaText}
              </p>
            </div>

            <div className="flex justify-center mb-12">
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-primary-foreground/20 bg-primary-foreground text-primary hover:bg-primary-foreground/90 px-8 py-6 rounded-full"
              >
                <a href="#download">
                  <Download className="mr-2 h-5 w-5" />
                  {t.contact.downloadButton}
                </a>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.contact.contacts.map((contact, idx) => (
                <div
                  key={contact.name}
                  className="flex items-center gap-4 bg-primary-foreground/5 rounded-xl p-4"
                >
                  <img
                    src={contactImages[idx]}
                    alt={contact.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <a
                      href={contactWebsites[idx]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-primary-foreground hover:text-accent transition-colors"
                    >
                      {contact.name}
                    </a>
                    <div className="text-sm text-primary-foreground/60 mb-1 whitespace-pre-line">
                      {renderRoleWithLinks(contact.role.replace(" & ", " &\n"), roleLinks[idx])}
                    </div>
                    <a
                      href={`mailto:${contactEmails[idx]}`}
                      className="text-sm text-accent hover:underline flex items-center gap-1"
                    >
                      {contactEmails[idx]}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16 pt-8 border-t border-primary-foreground/10"
        >
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <a href="https://intraprenoer.webflow.io/impressum" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">
              {t.contact.impressum}
            </a>
            <span className="text-primary-foreground/30">|</span>
            <a href="https://intraprenoer.webflow.io/datenschutz" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">
              {t.contact.datenschutz}
            </a>
          </div>
          <p className="text-sm text-primary-foreground/50">{t.contact.copyright}</p>
        </motion.div>
      </div>
    </section>
  );
}
